import React, { memo, useCallback, useMemo, useState } from 'react';
import { Icon, Modal, Text } from '@Sage/hoi-poi-ui';
import { SectionList } from '@Sage/ssm-web5';
import { getLiteral } from 'utils/getLiteral';
import { warningToast } from 'utils/toast';

import CONFIG from './TableConfig';
import CompanyRatesAddModal from './CompanyRatesAddModal';
import RatesTableSection from '../RatesTableSection';
import {
    useAppendToRatesModelList,
    useRatesModelListRef,
    removeModelListRow,
    toModelListArray,
} from '../shared/ratesModelListUtils';

import './styles.scss';

const MAX_COMPANIES = 200;

const deleteModalOverrides = {
    confirmButton: {
        type: 'primary-error',
    },
};

const CrudCompanyRates = memo(({ changeField, field = 'companyrates', modelList }) => {
    const dataSize = modelList ? modelList.length : 0;

    const onChange = useCallback(
        (fieldKey, value) => {
            changeField?.(fieldKey)(value);
        },
        [changeField],
    );

    const modelListRef = useRatesModelListRef(modelList);
    const appendToModelList = useAppendToRatesModelList({ field, modelListRef, onChange });

    // ========================================
    // LOCAL STATE
    // ========================================
    const [open, setOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState(null);

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const handleOpen = useCallback(() => {
        if (dataSize >= MAX_COMPANIES) {
            warningToast({
                text: getLiteral('label_price_list_exceeds_accounts'),
            });
            return;
        }
        setOpen(true);
    }, [dataSize]);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const openDeleteDialog = useCallback((row) => {
        setRowToDelete(row);
        setDeleteDialogOpen(true);
    }, []);

    const closeDeleteDialog = useCallback(() => {
        setDeleteDialogOpen(false);
        setRowToDelete(null);
    }, []);

    const confirmDeleteLine = useCallback(() => {
        const list = modelListRef.current;
        if (!list || !onChange || !rowToDelete) return;
        const rowIndex = list.findIndex((item) => String(item.id) === String(rowToDelete.id));
        if (rowIndex !== -1) {
            onChange(field, removeModelListRow(list, rowIndex));
        }
        closeDeleteDialog();
    }, [modelListRef, onChange, field, rowToDelete, closeDeleteDialog]);

    const getRowNodeId = useCallback((data) => String(data.id), []);

    const rowData = useMemo(() => {
        if (!modelList) return [];
        return toModelListArray(modelList).map((model, index) => ({
            id: model.id,
            companyname: model.companyname,
            numLine: index + 1,
        }));
    }, [modelList]);

    const columnDefs = useMemo(() => {
        const config = CONFIG();
        return config.columnDefs;
    }, []);

    const actions = useMemo(
        () => [
            (row) => ({
                icon: <Icon name="close" onClick={() => openDeleteDialog(row)} />,
                tooltip: { content: getLiteral('label_remove_from_list') },
            }),
        ],
        [openDeleteDialog],
    );

    const { domLayout, extraClassName } = useMemo(() => {
        if (!rowData?.length) {
            return {
                domLayout: 'normal',
                extraClassName: 'rates-crud-table--empty',
            };
        }
        if (rowData.length <= 5) {
            return { domLayout: 'autoHeight', extraClassName: '' };
        }
        return {
            domLayout: 'normal',
            extraClassName: 'rates-crud-table--scrollable',
        };
    }, [rowData]);

    return (
        <RatesTableSection
            addLabel={getLiteral('action_add')}
            className="companyRatesField"
            count={dataSize}
            emptyText={getLiteral('error_no_accounts_added_rate')}
            isEmpty={dataSize === 0}
            onAdd={handleOpen}
            sectionSubtitle={getLiteral('helptext_info_accounts_rates')}
            sectionTitle={getLiteral('label_info_accounts_rates')}
            widgetTitle={getLiteral('title_accounts')}
            footer={
                <>
                    <Modal
                        className="fm-delete-modal"
                        overlayClassName="fm-delete-modal__overlay"
                        overrides={deleteModalOverrides}
                        useCornerClose={false}
                        isOpen={deleteDialogOpen}
                        onCancel={closeDeleteDialog}
                        onConfirm={confirmDeleteLine}
                        title={getLiteral('label_remove_from_list')}
                        confirmText={getLiteral('action_confirm_bn')}
                        cancelText={getLiteral('action_cancel')}
                        size="small"
                    >
                        <Text>{getLiteral('confirm_sure')}</Text>
                    </Modal>
                    <CompanyRatesAddModal
                        title={getLiteral('title_accounts')}
                        handleClose={handleClose}
                        onAddLine={appendToModelList}
                        open={open}
                        companiesList={modelList}
                    />
                </>
            }
        >
            <SectionList
                id="company-rates-table"
                className={`rates-crud-table company-rates-table ${extraClassName}`}
                columnDefs={columnDefs}
                rowData={rowData}
                getRowNodeId={getRowNodeId}
                useSort={false}
                domLayout={domLayout}
                actions={actions}
            />
        </RatesTableSection>
    );
});

CrudCompanyRates.displayName = 'CrudCompanyRates';

export default CrudCompanyRates;
